import { createContext, useEffect, useReducer, useState } from 'react';

// third-party
import jwtDecode from 'jwt-decode';
import _ from 'lodash';

// project import
import axios from 'api/axios';
import authReducer, { LOGIN, LOGOUT, initialState } from 'contexts/reducer/auth';
import { Loading } from 'components';

// types
import { KeyedObject } from 'types';
import { AuthContextType, AuthDataProps } from 'types/auth';

const verifyToken: (st: string) => boolean = (authToken) => {
  if (!authToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(authToken);

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (authToken?: string | null) => {
  if (authToken) {
    localStorage.setItem('authToken', authToken);
    axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;
  } else {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| AUTH CONTEXT & PROVIDER ||============================== //

const AuthContext = createContext<AuthContextType | null>(null);

const createPayload = (data: any) => {
  const useData = _.omit(data, ['prepaid_devices']);
  const { orders, rental_devices, credit_cards, bank_accounts, certificates, ...customer } = useData;
  const bank_account = bank_accounts[0];
  return { orders, rental_devices, credit_cards, certificates, bank_account, customer } as AuthDataProps;
};

export const AuthProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [logoutDialog, setLogoutDialog] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      try {
        const authToken = window.localStorage.getItem('authToken');
        if (authToken && verifyToken(authToken)) {
          setSession(authToken);
          const { data: response } = await axios.get('/endpoint/customers');
          const { customer } = response.data;
          const isValidEmail = customer.email.indexOf('@') > -1;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              isValidEmail,
              ...createPayload(customer)
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    const website_id = process.env.REACT_APP_WEBSITE_ID;
    try {
      const { data: response } = await axios.post('/endpoint/customers/login', { email, password, website_id });
      if (!response.success) {
        throw new Error(response.error.message || 'Unknown error occurred');
      }
      const { token, customer } = response.data;
      setSession(token);
      const isValidEmail = customer.email.indexOf('@') > -1;
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isValidEmail,
          ...createPayload(customer)
        }
      });
    } catch (error) {
      throw new Error((error as Error).message || 'Unknown error occurred');
    }
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem('orderFormValues');
    localStorage.removeItem('extendFormValues');
    localStorage.removeItem('extendDevices');
    dispatch({ type: LOGOUT });
  };

  const refresh = async () => {
    try {
      const { data: response } = await axios.get('/endpoint/customers');
      if (!response.success) {
        throw new Error(response.error || 'Unknown error occurred');
      }
      const { customer } = response.data;
      const isValidEmail = customer.email.indexOf('@') > -1;
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isValidEmail,
          ...createPayload(customer)
        }
      });
    } catch (err) {
      dispatch({
        type: LOGOUT
      });
    }
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh, logoutDialog, setLogoutDialog }}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
